import { z } from "zod";
export const appVersion = "2.0.0-alpha.23";
export const roleSchema = z.enum([
    "super_admin",
    "organization_admin",
    "teacher",
    "student"
]);
export const privilegedRoles = ["super_admin", "organization_admin"];
export const teachingRoles = ["super_admin", "organization_admin", "teacher"];
export function hasAnyRole(userRoles, allowed) {
    return userRoles.some((role) => allowed.includes(role));
}
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().pipe(z.email()),
    password: z.string().min(8).max(256)
});
export const userSummarySchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    email: z.email(),
    displayName: z.string().min(1).max(120),
    roles: z.array(roleSchema),
    active: z.boolean()
});
export const learningEventTypeSchema = z.enum([
    "play_started",
    "play_heartbeat",
    "play_paused",
    "play_seeked",
    "play_completed",
    "group_loop_started",
    "group_loop_completed",
    "word_status_changed",
    "quiz_answered"
]);
/* ==========================================================================
 * 用户管理（Checkpoint 004）
 * ======================================================================== */
export const createUserSchema = z.object({
    email: z.string().trim().toLowerCase().pipe(z.email("请输入有效邮箱")),
    displayName: z.string().trim().min(1, "请填写姓名").max(120),
    password: z.string().min(10, "密码至少 10 位").max(200),
    roles: z.array(roleSchema).min(1, "至少选择一个角色"),
    mustChangePassword: z.boolean().default(true)
});
export const updateUserSchema = z.object({
    displayName: z.string().trim().min(1).max(120).optional(),
    roles: z.array(roleSchema).min(1).optional(),
    active: z.boolean().optional()
});
export const resetPasswordSchema = z.object({
    password: z.string().min(10, "密码至少 10 位").max(200),
    mustChangePassword: z.boolean().default(true)
});
export const roleLabels = {
    super_admin: "超级管理员",
    organization_admin: "机构管理员",
    teacher: "教师",
    student: "学生"
};
/* ==========================================================================
 * 模型管理（Checkpoint 004）
 * ======================================================================== */
export const modelProtocolSchema = z.enum(["openai_compatible", "anthropic_messages"]);
export const modelProtocolLabels = {
    openai_compatible: "OpenAI 兼容（/v1/chat/completions）",
    anthropic_messages: "Anthropic Messages（/v1/messages）"
};
export const createModelSchema = z.object({
    displayName: z.string().trim().min(1, "请填写名称").max(120),
    protocol: modelProtocolSchema,
    apiBaseUrl: z.string().trim().pipe(z.url("请输入完整的 API 地址")),
    apiKey: z.string().trim().min(1, "请填写 API Key").max(500),
    modelName: z.string().trim().min(1, "请填写模型名").max(160),
    enabled: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    settings: z
        .object({
        temperature: z.number().min(0).max(2).optional(),
        maxTokens: z.number().int().min(1).max(200000).optional(),
        timeoutMs: z.number().int().min(1000).max(300000).optional()
    })
        .default({})
});
// 更新时 apiKey 可省略，表示保持原密钥不变
export const updateModelSchema = createModelSchema.partial().extend({
    apiKey: z.string().trim().max(500).optional()
});
/* ==========================================================================
 * 学情分析（Checkpoint 004）
 * ======================================================================== */
export const analyticsScopeSchema = z.object({
    scope: z.enum(["organization", "class", "student"]).default("organization"),
    targetId: z.uuid().optional(),
    days: z.number().int().min(1).max(180).default(14),
    modelId: z.uuid().optional()
});
/* ==========================================================================
 * 学习事件上报
 * ======================================================================== */
export const learningEventBatchSchema = z.object({
    events: z
        .array(z.object({
        eventType: learningEventTypeSchema,
        occurredAt: z.iso.datetime(),
        songNumber: z.string().max(8).optional(),
        variantKey: z.string().max(24).optional(),
        durationMs: z.number().int().min(0).max(86_400_000).optional(),
        positionMs: z.number().int().min(0).max(86_400_000).optional(),
        metadata: z.record(z.string(), z.unknown()).optional()
    }))
        .min(1)
        .max(100)
});
/* ==========================================================================
 * 歌词时间轴（逐行同步）
 * ======================================================================== */
export const lyricLineSchema = z.object({
    lineIndex: z.number().int().min(0),
    lineKey: z.string().max(32),
    section: z.string().max(80),
    text: z.string().max(500),
    startMs: z.number().int().min(0).max(3_600_000),
    endMs: z.number().int().min(0).max(3_600_000)
});
export const saveLyricTrackSchema = z.object({
    publish: z.boolean().default(false),
    lines: z.array(lyricLineSchema).max(400)
});
//# sourceMappingURL=index.js.map